import { getLanguageLocale } from "@captions-web/i18n/utils/getLanguageLocale";
import { addItemToArray } from "@captions-web/utils/immutable";
import { createUseStoreWithSelectorDeferred } from "@captions-web/utils/zustand-store";
import i18next from "i18next";
import { nanoid } from "nanoid";
import { createStore } from "zustand";
import { combine } from "zustand/middleware";

// import { getIterableStore } from "./iterable";
import { getAuthStore } from "~/modules/auth/auth-store";
import { getConfigStore } from "~/stores/config";
import { createStoreGetters } from "~/stores/root";

import { addDatadogAction } from "./datadog";
import { getMixpanelStore } from "./mixpanel";
import { getStatsigStore } from "./statsig";

export type AnalyticsTool = "mixpanel" | "statsig" | "googletagmanager" | "iterable" | "datadog";

export const analyticsTool = {
  defaults: ["mixpanel", "statsig", "googletagmanager", "datadog"],
  all: ["mixpanel", "statsig", "googletagmanager", "iterable", "datadog"],
} satisfies Record<string, AnalyticsTool[]>;

const parseNonStringPropsForStatsig = (properties: Record<string, unknown> = {}) => {
  return Object.entries(properties).reduce(
    (acc, [key, value]) => {
      if (typeof value === "string") {
        acc[key] = value;
      } else {
        acc[key] = JSON.stringify(value);
      }

      return acc;
    },
    {} as Record<string, string>
  );
};

const getOrgEntitlement = () => {
  const { currentOrg } = getAuthStore();
  if (!currentOrg) {
    return null;
  }

  return currentOrg.subscription?.plan ?? "free";
};

export class Analytics {
  static track(
    event: string,
    additionalProperties?: Record<string, unknown> | void,
    tools: AnalyticsTool[] = analyticsTool.defaults
  ) {
    const properties = {
      org_entitlement: getOrgEntitlement(),
      user_locale_setting: getLanguageLocale(i18next),
      commit_sha: getConfigStore().env.COMMIT_SHA,
      branch: getConfigStore().env.BRANCH,
      ...additionalProperties,
    };

    if (tools.includes("mixpanel")) {
      getMixpanelStore().track(event, properties);
    }

    if (tools.includes("statsig")) {
      const normalizedProperties = parseNonStringPropsForStatsig(
        properties as Record<string, string>
      );
      getStatsigStore().track(event, normalizedProperties);
    }

    if (tools.includes("googletagmanager")) {
      if (window?.dataLayer) {
        window.dataLayer.push({ event });
      }
    }

    if (tools.includes("iterable")) {
      // getIterableStore().track(event, properties);
    }

    if (tools.includes("datadog")) {
      addDatadogAction(event, properties);
    }

    // always log in dev otherwise check feature flag
    if (process.env.NODE_ENV === "development") {
      console.log("🌐 %c" + event, "color: blue; font-weight: bold;", properties);
    }

    getEventStreamStore().addEvent({
      id: nanoid(),
      name: event,
      timestamp: Date.now(),
      properties,
      stores: tools,
    });
  }

  static trackConversion() {
    // google tag conversion event
    // note that gtag will not exist on native
    // if (window?.gtag) {
    //   window.gtag("event", "conversion", {
    //     send_to: "AW-428533084/EwzwCKnE94cZENzKq8wB",
    //   });
    // }
  }
}

export interface EventStreamItem {
  id: string;
  name: string;
  timestamp: number;
  properties: Record<string, unknown>;
  stores: string[];
}

export function createEventStreamStore() {
  return createStore(
    combine(
      {
        events: [] as EventStreamItem[],
        isOpen: false,
      },
      (set) => ({
        async init() {},
        addEvent: (event: EventStreamItem) =>
          set((state) => ({ events: addItemToArray(state.events, event, { position: "front" }) })),
        clear: () => set({ events: [] }),
        toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),
      })
    )
  );
}

type EventStreamStore = ReturnType<ReturnType<typeof createEventStreamStore>["getState"]>;

export const {
  getStore: getEventStreamStore,
  getStoreAsync: getEventStreamStoreAsync,
  getStoreApi: getEventStreamStoreApi,
} = createStoreGetters<EventStreamStore>("event-stream");

export const useEventStreamStore = createUseStoreWithSelectorDeferred(() =>
  getEventStreamStoreApi()
);
